// Logica compartilhada de "registrar tentativa de contato", usada pelo unico
// componente reusado em Fila / Ficha / Drawer do Kanban (decisao de design:
// nao ha implementacoes divergentes desta acao).
//
// Duas escritas por registro:
//   1) INSERT em contact_attempts — dispara os triggers de banco que validam
//      nao_contatar/canal_permitido e recalculam atividade (ultima_tentativa_em,
//      canal_permitido etc. — o cliente NUNCA escreve essas colunas direto).
//   2) UPDATE em leads (etapa/proximo_contato_em/motivo) — decisao de PRODUTO
//      de qual etapa vem a seguir, que nao existe como regra no banco (o
//      schema so audita a mudanca de etapa via trigger, nao a decide).
import { anularContactAttempt, createContactAttempt, updateLead, type LeadPatch } from "./leads";
import type { ContactAttempt, ContactChannel, ContactResult, Lead, LeadStage } from "./types";

/** Para onde o lead deveria ir no funil apos um resultado de tentativa.
 * "Registrar tentativa move o card no kanban automaticamente" (decisao de
 * design, nao reabrir). */
export function proximaEtapaPara(
  etapaAtual: LeadStage,
  resultado: ContactResult,
): LeadStage {
  if (resultado === "recusou") return "recusou";

  if (resultado === "atendeu" || resultado === "pediu_retorno") {
    if (etapaAtual === "novo" || etapaAtual === "nao_atendeu") {
      return "tentando_contato";
    }
    return etapaAtual;
  }

  // nao_atendeu / caixa_postal / numero_invalido: nao houve resposta.
  if (etapaAtual === "novo") return "tentando_contato";
  if (etapaAtual === "tentando_contato") return "nao_atendeu";
  return etapaAtual;
}

/** Sugestão de próximo contato quando o usuário não escolhe uma data
 * explicitamente (ex.: "Atendeu" sem reagendar reunião). Sempre editável na UI. */
export function sugerirProximoContato(resultado: ContactResult): Date | null {
  if (resultado === "recusou") return null;
  const dias = resultado === "atendeu" ? 3 : 1;
  const data = new Date();
  data.setDate(data.getDate() + dias);
  return data;
}

export interface RegistrarTentativaInput {
  lead: Lead;
  canal: ContactChannel;
  resultado: ContactResult;
  autorId: string;
  nota?: string;
  /** Obrigatório quando resultado === "recusou" (motivos categoria "recusa"). */
  motivoId?: string | null;
  motivoOutro?: string | null;
  /** ISO. Se omitido, usa sugerirProximoContato(resultado). */
  proximoContatoEm?: string | null;
}

export interface RegistrarTentativaResultado {
  attempt: ContactAttempt;
  leadAtualizado: Lead;
  /** Estado anterior do lead nos campos alterados — usado por
   * desfazerRegistroTentativa() para reverter com precisão no "Desfazer". */
  leadAnteriorPatch: LeadPatch;
}

export async function registrarTentativa(
  input: RegistrarTentativaInput,
): Promise<RegistrarTentativaResultado> {
  const { lead, canal, resultado, autorId, nota, motivoId, motivoOutro } = input;

  if (resultado === "recusou" && !motivoId) {
    throw new Error("Selecione um motivo de recusa antes de confirmar.");
  }

  const attempt = await createContactAttempt({
    leadId: lead.id,
    canal,
    resultado,
    autorId,
    nota: nota && nota.trim() !== "" ? nota.trim() : null,
  });

  const novaEtapa = proximaEtapaPara(lead.etapa, resultado);
  const novoProximoContato =
    resultado === "recusou"
      ? null
      : (input.proximoContatoEm ??
        sugerirProximoContato(resultado)?.toISOString() ??
        lead.proximo_contato_em);

  const leadAnteriorPatch: LeadPatch = {
    etapa: lead.etapa,
    proximo_contato_em: lead.proximo_contato_em,
    motivo_id: lead.motivo_id,
    motivo_outro: lead.motivo_outro,
  };

  const patch: LeadPatch = {
    etapa: novaEtapa,
    proximo_contato_em: novoProximoContato,
    motivo_id: resultado === "recusou" ? (motivoId ?? null) : lead.motivo_id,
    motivo_outro: resultado === "recusou" ? (motivoOutro ?? null) : lead.motivo_outro,
  };

  const leadAtualizado = await updateLead(lead.id, patch);

  return { attempt, leadAtualizado, leadAnteriorPatch };
}

export interface DesfazerRegistroInput {
  attemptId: string;
  leadId: string;
  autorId: string;
  leadAnteriorPatch: LeadPatch;
}

/** Toast "Desfazer" (5-6s). Anula a tentativa (nunca DELETE — historico
 * append-only) e reverte a etapa/proximo_contato/motivo do lead. */
export async function desfazerRegistroTentativa(
  input: DesfazerRegistroInput,
): Promise<void> {
  await anularContactAttempt(
    input.attemptId,
    input.autorId,
    "Desfeito pelo usuário logo após o registro (ação Desfazer).",
  );
  await updateLead(input.leadId, input.leadAnteriorPatch);
}
